import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorTriggerProps } from "@/v2-backoffice-client/components/selector"
import { usePinnedResults } from "@/v2-backoffice-client/components/selector/selector.hooks"
import { useTenantQuery } from "@/v2-backoffice-client/tenant/tenant.query"
import UserTenantTypePicker from "@/v2-backoffice-client/user/tenant/type/UserTenantTypePicker"
import TenantUserAddManySelectorItem from "@/v2-backoffice-client/tenant/user/add/many/TenantUserAddManySelectorItem"

export type TenantUserAddManySelectorProps = SelectorTriggerProps & {
  userIds: number[]
  existingTenantIds?: number[]
  onCompleted?(): void
}

function TenantUserAddManySelector(props: TenantUserAddManySelectorProps) {
  const {
    userIds,
    existingTenantIds = [],
    onCompleted,
    ...triggerProps
  } = props

  const [search, setSearch] = useState("")
  const [addedIds, setAddedIds] = useState<Set<number>>(new Set())
  const [userTenantTypeCode, setUserTenantTypeCode] = useState<number>(2)

  const { data, loading, error } = useTenantQuery({
    skip: search.length < 2,
    variables: {
      input: {
        limit: 10,
        filters:
          search.length >= 2
            ? [{ column: "Name", contains: search }]
            : undefined,
      },
    },
  })

  errorToast(error, {
    title: "Failed to query for teams",
  })

  const tenants = data?.getTenants.items ?? []
  const pinnedTenants = usePinnedResults({
    results: tenants,
    isPinned: (t) => existingTenantIds.includes(t.Id) || addedIds.has(t.Id),
  })

  return (
    <Selector
      onOpenChange={(open) => {
        if (!open) setAddedIds(new Set())
      }}
    >
      <Selector.Trigger iconSrc="users" {...triggerProps}>
        {triggerProps.children ?? "Assign Users to Team"}
      </Selector.Trigger>

      <Selector.Content>
        <UserTenantTypePicker
          className="m-2"
          value={userTenantTypeCode}
          onChange={setUserTenantTypeCode}
          size="sm"
        />

        <Selector.Search
          placeholder="Search by team name..."
          onSearch={setSearch}
        />

        <Selector.List
          emptyLabel="No teams found"
          isLoading={loading}
          error={error}
          noResults={tenants.length === 0}
          pinned={pinnedTenants.map((tenant) => (
            <TenantUserAddManySelectorItem
              key={tenant.Id}
              tenant={tenant}
              userIds={userIds}
              userTenantTypeCode={userTenantTypeCode}
              added
              onSelect={() => {
                setAddedIds((prev) => new Set(prev).add(tenant.Id))
                onCompleted?.()
              }}
            />
          ))}
        >
          {tenants.map((tenant) => (
            <TenantUserAddManySelectorItem
              key={tenant.Id}
              tenant={tenant}
              userIds={userIds}
              userTenantTypeCode={userTenantTypeCode}
              added={
                existingTenantIds.includes(tenant.Id) || addedIds.has(tenant.Id)
              }
              onSelect={() => {
                setAddedIds((prev) => new Set(prev).add(tenant.Id))
                onCompleted?.()
              }}
            />
          ))}
        </Selector.List>
      </Selector.Content>
    </Selector>
  )
}

export default TenantUserAddManySelector
